/**
 * Terminal "no content" state of a document: the bytes were never stored and never will be,
 * because the archive document exceeds the import cap or the archive cannot read it. Distinct
 * from the transient pre-upload state, which resolves on its own, and from a prestage failure,
 * which means bytes exist but could not be read.
 */
import { z } from 'zod';
/**
 * Largest archive document imported as bytes; anything above is recorded without content.
 * The same 128 MB the readers take, so a file that is imported is also read out for the AI.
 */
export const MAX_ARCHIVE_IMPORT_BYTES = 128 * 1024 * 1024;
/**
 * Why a record carries no content: the size that kept the bytes out, or the archive's own read
 * error on a file it lists but cannot deliver.
 */
export const DocumentContentUnavailable = z.discriminatedUnion('reason', [
    z.strictObject({
        reason: z.literal('too-large'),
        // null: the download itself exceeded the cap, so the total size was never established.
        sizeBytes: z.number().int().nonnegative().nullable(),
    }),
    z.strictObject({
        reason: z.literal('archive-unreadable'),
    }),
]);
/** True when the document's blob does not exist by decision; `undefined` is a record that predates the flag. */
export function isDocumentContentUnavailable(document) {
    return document.contentUnavailable !== undefined && document.contentUnavailable !== null;
}
/**
 * The cause in the words clerks and the AI read, as one phrase: "Datei zu groß (130 MB)", or that the
 * archive cannot read the file. A size is given in whole megabytes; an unknown one names the cap it exceeded.
 */
export function describeUnavailableContent(contentUnavailable) {
    switch (contentUnavailable.reason) {
        case 'too-large': {
            const size = contentUnavailable.sizeBytes === null
                ? `> ${Math.round(MAX_ARCHIVE_IMPORT_BYTES / (1024 * 1024))} MB`
                : `${Math.round(contentUnavailable.sizeBytes / (1024 * 1024))} MB`;
            return `Datei zu groß (${size})`;
        }
        case 'archive-unreadable':
            return 'Datei im Archiv nicht lesbar';
        default: {
            const unknownReason = contentUnavailable;
            throw new Error(`Unknown content-unavailable reason ${JSON.stringify(unknownReason)}`);
        }
    }
}
